import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import CssBaseline from "@material-ui/core/CssBaseline";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Link from "@material-ui/core/Link";
import { Link as RouterLink } from "react-router-dom";

import { toast } from "react-toastify";

import LOGO from "src/assets/svgs/logo.png";
import type { Theme } from "src/theme";
import { supportGetToken } from "src/store/support/selector";
import {
  setSupportLoginToken,
  accessClientAccount,
} from "src/store/support/actions";
import {
  listAccountantClients,
  acceptInvitationWithJwt,
  rejectInvitationWithJwt,
  getAccountantClientLogoUrl,
  type AccountantClient,
} from "src/apiService/modules/accountant";
import LoadableButton from "src/components/LoadableButton";

const useStyles = makeStyles((theme: Theme) => {
  // Accountant / support portals always render under ForcedLightTheme (default
  // light palette) — do not branch on dark mode or custom brand colors.
  const pageBg = theme.palette.background.default;
  const titleOnBg = theme.palette.text.primary;
  const titleOnCard = theme.palette.text.primary;
  const brandPrimaryLight = theme.palette.primary.light;

  return {
    root: {
      minHeight: "100vh",
      backgroundColor: pageBg,
      display: "flex",
      flexDirection: "column",
    },
    topbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      padding: theme.spacing(2, 4),
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: "transparent",
    },
    topbarLeft: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(3),
      minWidth: 0,
    },
    logo: {
      height: theme.spacing(7),
      width: "auto",
      display: "block",
    },
    titleBlock: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    },
    title: {
      margin: 0,
      color: titleOnBg,
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.h5.fontSize,
      lineHeight: 1.25,
    },
    subtitle: {
      color: titleOnBg,
      opacity: 0.75,
      fontSize: 13,
      marginTop: 2,
    },
    topbarRight: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexShrink: 0,
    },
    email: {
      fontSize: 13,
      color: titleOnBg,
      opacity: 0.85,
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
    iconButton: {
      color: titleOnBg,
      transition: "transform 0.2s ease",
      "&:hover": {
        transform: "scale(1.1)",
        backgroundColor: "transparent",
      },
    },
    content: {
      flex: 1,
      padding: theme.spacing(3, 4),
      maxWidth: 980,
      width: "100%",
      margin: "0 auto",
      boxSizing: "border-box",
    },
    search: {
      marginBottom: theme.spacing(1),
    },
    searchInputRoot: {
      backgroundColor: theme.palette.background.paper,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.divider,
      },
      "&:hover": {
        boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
        transform: "translateY(-2px)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.divider,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
    searchInput: {
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
    },
    countLabel: {
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      fontSize: 11,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      margin: theme.spacing(2, 0, 1.5),
    },
    list: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
    row: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      padding: theme.spacing(2, 2.5),
      borderRadius: theme.shape.borderRadius + 2,
      backgroundColor: theme.palette.background.paper,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      border: `1px solid ${theme.palette.divider}`,
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
        transform: "translateY(-1px)",
      },
    },
    rowInfo: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.75),
      minWidth: 0,
    },
    clientLogo: {
      width: 44,
      height: 44,
      fontSize: 13,
      fontWeight: 600,
      background: brandPrimaryLight,
      color: theme.palette.getContrastText(brandPrimaryLight),
      position: "relative",
    },
    clientLogoImg: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    clientLogoMuted: {
      background: theme.palette.action.hover,
      color: theme.palette.text.secondary,
    },
    clientLogoSpinner: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme.palette.background.paper,
      borderRadius: "50%",
    },
    clientName: {
      fontSize: 15,
      fontWeight: theme.typography.fontWeightMedium,
      margin: 0,
      color: titleOnCard,
    },
    clientMeta: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.75),
    },
    pendingPill: {
      display: "inline-flex",
      alignItems: "center",
      fontSize: 11,
      fontWeight: 600,
      padding: "1px 8px",
      borderRadius: 12,
      background: theme.palette.warning.light,
      color: theme.palette.getContrastText(theme.palette.warning.light),
    },
    rowActions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexShrink: 0,
    },
    emptyState: {
      padding: theme.spacing(5, 4),
      textAlign: "center",
      borderRadius: theme.shape.borderRadius + 2,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    emptyEmailRow: {
      display: "inline-flex",
      alignItems: "center",
      gap: theme.spacing(1),
      background: pageBg,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1, 1, 1, 1.75),
      marginTop: theme.spacing(2),
    },
    mono: { fontFamily: "Menlo, Monaco, monospace", fontSize: 13 },
  };
});

function initials(name: string, fallback: string): string {
  const source = name.trim() || fallback;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const relativeTime = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/** "Last accessed 2 days ago" style relative string. */
function lastAccessedLabel(epochMillis: number | null): string {
  if (!epochMillis) return "Never accessed";
  const days = Math.floor((Date.now() - epochMillis) / 86_400_000);
  const [value, unit]: [number, Intl.RelativeTimeFormatUnit] =
    days < 7
      ? [days, "day"]
      : days < 35
      ? [Math.floor(days / 7), "week"]
      : [Math.floor(days / 30), "month"];
  return `Last accessed ${relativeTime.format(-Math.max(value, 0), unit)}`;
}

function clientSortName(client: AccountantClient): string {
  return (client.companyName || client.ownerEmail || "").trim().toLowerCase();
}

/** Pending invites first, then active; alphabetical by company name / email. */
function compareClients(a: AccountantClient, b: AccountantClient): number {
  if (a.status !== b.status) {
    return a.status === "pending" ? -1 : 1;
  }
  return clientSortName(a).localeCompare(clientSortName(b));
}

function clientMatchesSearch(client: AccountantClient, q: string): boolean {
  return (
    client.companyName.toLowerCase().includes(q) ||
    client.ownerEmail.toLowerCase().includes(q)
  );
}

interface ClientAvatarProps {
  client: AccountantClient;
  token: string;
  muted?: boolean;
}

function ClientAvatar({ client, token, muted }: ClientAvatarProps) {
  const classes = useStyles();
  const label = client.companyName || client.ownerEmail;
  const letters = initials(client.companyName, client.ownerEmail);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    client.hasCompanyLogo ? "loading" : "idle"
  );

  useEffect(() => {
    if (!client.hasCompanyLogo) {
      setStatus("idle");
      return;
    }
    setStatus("loading");
  }, [client.hasCompanyLogo, client.invitationId, token]);

  if (!client.hasCompanyLogo || status === "error" || status === "idle") {
    return (
      <Avatar
        className={`${classes.clientLogo} ${
          muted ? classes.clientLogoMuted : ""
        }`}
        alt={label}
      >
        {letters}
      </Avatar>
    );
  }

  const src = getAccountantClientLogoUrl(client.invitationId, token);

  return (
    <Avatar
      className={classes.clientLogo}
      alt={label}
      src={status === "ready" || status === "loading" ? src : undefined}
      imgProps={{
        className: classes.clientLogoImg,
        onLoad: () => setStatus("ready"),
        onError: () => setStatus("error"),
      }}
    >
      {status === "loading" ? (
        <span className={classes.clientLogoSpinner}>
          <CircularProgress size={18} thickness={5} />
        </span>
      ) : (
        letters
      )}
    </Avatar>
  );
}

interface ClientRowProps {
  client: AccountantClient;
  token: string;
  busy: boolean;
  onAccess: (client: AccountantClient) => void;
  onAccept: (client: AccountantClient) => void;
  onReject: (client: AccountantClient) => void;
}

function ClientRow({
  client,
  token,
  busy,
  onAccess,
  onAccept,
  onReject,
}: ClientRowProps) {
  const classes = useStyles();
  const isPending = client.status === "pending";
  const label = client.companyName || client.ownerEmail;
  return (
    <Paper className={classes.row} elevation={0}>
      <Box className={classes.rowInfo}>
        <ClientAvatar client={client} token={token} muted={isPending} />
        <Box minWidth={0}>
          <Typography className={classes.clientName}>{label}</Typography>
          <Typography className={classes.clientMeta} noWrap component="div">
            {isPending ? (
              <>
                <span className={classes.pendingPill}>Pending invite</span>
                {client.companyName && client.ownerEmail ? (
                  <>
                    <span>·</span>
                    <span>{client.ownerEmail}</span>
                  </>
                ) : null}
              </>
            ) : (
              <>
                {client.companyName && client.ownerEmail ? (
                  <>
                    <span>{client.ownerEmail}</span>
                    <span>·</span>
                  </>
                ) : null}
                <span>{lastAccessedLabel(client.lastAccessed)}</span>
              </>
            )}
          </Typography>
        </Box>
      </Box>
      {isPending ? (
        <Box className={classes.rowActions}>
          <Button
            variant="outlined"
            size="small"
            color="default"
            disabled={busy}
            onClick={() => onReject(client)}
          >
            Reject
          </Button>
          <LoadableButton
            variant="contained"
            size="small"
            color="primary"
            loading={busy}
            disabled={busy}
            onClick={() => onAccept(client)}
          >
            Accept
          </LoadableButton>
        </Box>
      ) : (
        <LoadableButton
          variant="contained"
          size="small"
          color="primary"
          loading={busy}
          disabled={busy}
          endIcon={<OpenInNewIcon fontSize="small" />}
          onClick={() => onAccess(client)}
        >
          Access account
        </LoadableButton>
      )}
    </Paper>
  );
}

function MyClients() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const token = useSelector(supportGetToken);

  const [loading, setLoading] = useState(true);
  const [accountantEmail, setAccountantEmail] = useState("");
  const [clients, setClients] = useState<AccountantClient[]>([]);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await listAccountantClients(token);
        if (cancelled) return;
        setAccountantEmail(result.accountantEmail);
        setClients(result.clients);
      } catch (e) {
        if (cancelled) return;
        // Expired / invalid session: clear the stored JWT so the router lands
        // on the accountant login page (standard re-auth flow).
        if ((e as { code?: string }).code === "unauthenticated") {
          dispatch(setSupportLoginToken());
          return;
        }
        toast.error(`Couldn't load your clients: ${(e as Error).message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, dispatch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? clients.filter((c) => clientMatchesSearch(c, q)) : clients;
    return [...list].sort(compareClients);
  }, [clients, search]);

  const handleAccess = useCallback(
    async (client: AccountantClient) => {
      setBusyId(client.invitationId);
      try {
        await dispatch(accessClientAccount(client.ownerEmail) as any);
      } catch (e) {
        const name = client.companyName || client.ownerEmail;
        const code = (e as { code?: string }).code;
        toast.error(
          code === "permission-denied"
            ? `Access to ${name} has been revoked.`
            : `Couldn't open ${name}: ${(e as Error).message}`
        );
      } finally {
        setBusyId(null);
      }
    },
    [dispatch]
  );

  const handleAccept = useCallback(
    async (client: AccountantClient) => {
      setBusyId(client.invitationId);
      try {
        await acceptInvitationWithJwt(token, client.invitationId);
        setClients((prev) =>
          prev.map((c) =>
            c.invitationId === client.invitationId
              ? { ...c, status: "active", uid: c.uid || undefined }
              : c
          )
        );
        toast.success(
          `Accepted access to ${client.companyName || client.ownerEmail}.`
        );
      } catch (e) {
        toast.error(
          `Couldn't accept invitation: ${(e as Error).message || "try again."}`
        );
      } finally {
        setBusyId(null);
      }
    },
    [token]
  );

  const handleReject = useCallback(
    async (client: AccountantClient) => {
      setBusyId(client.invitationId);
      try {
        await rejectInvitationWithJwt(token, client.invitationId);
        setClients((prev) =>
          prev.filter((c) => c.invitationId !== client.invitationId)
        );
        toast.success(
          `Rejected invitation from ${client.companyName || client.ownerEmail}.`
        );
      } catch (e) {
        toast.error(
          `Couldn't reject invitation: ${(e as Error).message || "try again."}`
        );
      } finally {
        setBusyId(null);
      }
    },
    [token]
  );

  const handleSignOut = useCallback(() => {
    setMenuAnchor(null);
    setProfileOpen(false);
    dispatch(setSupportLoginToken());
  }, [dispatch]);

  const handleOpenProfile = useCallback(() => {
    setMenuAnchor(null);
    setProfileOpen(true);
  }, []);

  const handleCopyEmail = useCallback(() => {
    navigator.clipboard.writeText(accountantEmail);
    toast.success("Email copied.");
  }, [accountantEmail]);

  return (
    <Box className={classes.root}>
      <CssBaseline />
      <Box className={classes.topbar}>
        <Box className={classes.topbarLeft}>
          <img src={LOGO} alt="My Reseller Genie" className={classes.logo} />
          <Box className={classes.titleBlock}>
            <h1 className={classes.title}>My Clients</h1>
            <div className={classes.subtitle}>
              Select a client to access their account
            </div>
          </Box>
        </Box>
        <Box className={classes.topbarRight}>
          {accountantEmail ? (
            <span className={classes.email}>{accountantEmail}</span>
          ) : null}
          <IconButton
            className={classes.iconButton}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label="account menu"
          >
            <AccountCircleIcon fontSize="large" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem onClick={handleOpenProfile}>
              <PersonOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={handleSignOut}>
              <ExitToAppIcon fontSize="small" style={{ marginRight: 8 }} />
              Sign out
            </MenuItem>
          </Menu>
          <Dialog
            open={profileOpen}
            onClose={() => setProfileOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Profile</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Signed in as
              </Typography>
              <Typography variant="body1" className={classes.mono}>
                {accountantEmail || "—"}
              </Typography>
              <Typography variant="body2" style={{ marginTop: 16 }}>
                <Link
                  component={RouterLink}
                  to="/accountant/forgot-password"
                  onClick={() => setProfileOpen(false)}
                >
                  Reset password
                </Link>
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setProfileOpen(false)} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>

      <Box className={classes.content}>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        ) : clients.length === 0 ? (
          <Paper className={classes.emptyState} elevation={0}>
            <Typography variant="h6" gutterBottom>
              No clients yet
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Your clients can add you from their My Reseller Genie account by
              entering your email address below. Once they send the invite,
              their account will appear here.
            </Typography>
            <Box className={classes.emptyEmailRow}>
              <span className={classes.mono}>{accountantEmail}</span>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<FileCopyOutlinedIcon fontSize="small" />}
                onClick={handleCopyEmail}
              >
                Copy
              </Button>
            </Box>
          </Paper>
        ) : (
          <>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={classes.search}
              InputProps={{
                classes: {
                  root: classes.searchInputRoot,
                  input: classes.searchInput,
                },
              }}
            />
            <Typography className={classes.countLabel}>
              {filtered.length} {filtered.length === 1 ? "client" : "clients"}
            </Typography>
            <Box className={classes.list}>
              {filtered.map((client) => (
                <ClientRow
                  key={client.invitationId}
                  client={client}
                  token={token || ""}
                  busy={busyId === client.invitationId}
                  onAccess={handleAccess}
                  onAccept={handleAccept}
                  onReject={handleReject}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default MyClients;
